import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Input,
  Typography,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import CustomButton from '../global/CustomButton';
import { useLocalState } from '../../utils/hooks/global/useLocalStorage';

const REASON_TEXT = {
  'user-not-found': 'No account with that email.',
  'schema-mismatch': 'Unexpected credential schema.',
  'issuer-mismatch': 'Credential not issued by this service.',
  'bad-offer': 'Malformed offer from wallet.',
  'credential-revoked': 'Credentials are revoked',
  expired: 'Challenge expired. Please try again.',
  failed: 'Authentication failed. Try again.',
  timeout: 'No response from wallet.',
};

const SignifyAuth = () => {
  const [showInput, setShowInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('enterEmail');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const allowedDomains = ['cardanofoundation.org'];
  const [email, setEmail] = useLocalState(null, 'email');
  const [formEmail, setFormEmail] = useState('');

  useEffect(() => {
    if (email != null) {
      setFormEmail(email);
    }
  }, [email]);

  function isInternalEmail(v) {
    const m = v
      .trim()
      .toLowerCase()
      .match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
    if (!m) return false;
    const domain = v.split('@')[1]?.toLowerCase();
    return allowedDomains.length ? allowedDomains.includes(domain) : true;
  }

  const reasonToMessage = (reason, fallback = 'Authentication failed') =>
    REASON_TEXT[reason] || fallback;

  const startPolling = (requestId, pollToken) => {
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`${backendUrl}/auth/status/${requestId}`, {
          method: 'GET',
          headers: { 'x-poll-token': pollToken },
          cache: 'no-store',
        });

        if (res.status === 401) {
          clearInterval(pollInterval);
          setStatus('failed');
          setError('Unauthorized or invalid poll token');
          return;
        }

        if (!res.ok) {
          clearInterval(pollInterval);
          setStatus('failed');
          setError(`Server error (${res.status})`);
          return;
        }

        const data = await res.json();

        if (data.success === true && data.jwt) {
          clearInterval(pollInterval);
          setStatus('verified');
          setTimeout(() => {
            window.location.href = `/?jwt=${data.jwt}`;
          }, 1500);
          return;
        }

        if (data.status === 'waiting') return;

        if (data.success === false) {
          clearInterval(pollInterval);
          const msg = reasonToMessage(
            data.reason,
            data.status === 'expired'
              ? 'Challenge expired. Please try again.'
              : 'Authentication failed'
          );
          setStatus(data.status || 'failed');
          setError(msg);
          return;
        }
      } catch (err) {
        clearInterval(pollInterval);
        setStatus('failed');
        setError('Connection error during verification');
      }
    }, 2000);

    setTimeout(
      () => {
        clearInterval(pollInterval);
        if (status === 'waiting') {
          setStatus('expired');
          setError('Challenge expired. Please generate a new one.');
        }
      },
      3 * 60 * 1000
    );
  };

  const handleVerificationSuccess = async () => {
    try {
      const res = await fetch(`${backendUrl}/auth/login/veridian`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formEmail }),
      });
      const data = await res.json();

      if (!res.ok || data.success === false) {
        setStatus('failed');
        setError(data.error || 'Login request failed');
        return;
      }

      const { requestId, pollToken } = data;
      if (!requestId || !pollToken) {
        setStatus('failed');
        setError('Malformed response from server');
        return;
      }

      setStatus('waiting');
      startPolling(requestId, pollToken);
    } catch (err) {
      console.error('Veridian login error:', err);
      setStatus('failed');
      setError('Network error starting login');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'enterEmail':
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Enter your internal email
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: '25px',
              }}
            >
              <Input
                placeholder="Internal email"
                type="email"
                required
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                sx={{ width: '100%' }}
              />
              <CustomButton
                buttonText={'Login'}
                icon={VpnKeyIcon}
                color={'black'}
                onClick={() => {
                  if (isInternalEmail(formEmail)) {
                    setEmail(formEmail);
                    handleVerificationSuccess();
                  }
                }}
                sx={{ mt: 4 }}
              />
            </Box>
          </Box>
        );

      case 'waiting':
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Scan QR Code with Identity Wallet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Check the Veridian Wallet notifications to share the credentials
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 2,
              }}
            >
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2">
                Waiting for verification...
              </Typography>
            </Box>
          </Box>
        );

      case 'verified':
        return (
          <Box textAlign="center">
            <Typography
              variant="h4"
              color="success.main"
              gutterBottom
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              gap={'10px'}
            >
              <CheckIcon fontSize="large" />
              Authentication successful
            </Typography>
            <Typography variant="body2">Redirecting to dashboard...</Typography>
          </Box>
        );

      case 'failed':
      case 'expired':
        return (
          <Box textAlign="center">
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <CustomButton
              color="black"
              buttonText={'Try Again'}
              icon={ReplayIcon}
              onClick={() => {
                setStatus('enterEmail');
                setError('');
                setShowInput(false);
              }}
              disabled={loading}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card sx={{ width: 400, mx: 'auto', mt: 4 }}>
      <CardContent sx={{ p: 3 }}>{renderContent()}</CardContent>
    </Card>
  );
};

export default SignifyAuth;
